import { Button, Checkbox, Input, message, Modal, Popconfirm, Select, Space, Spin, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    getStaleAccountsCount,
    getStaleAccounts,
    blockStaleAccounts,
    unblockStaleAccounts,
    delete18MonthIdleAccounts
} from 'services';
import { convertDateTime } from 'utils';
import { ExportOutlined } from '@ant-design/icons';

const { Search } = Input;
const { Option } = Select;

type StaleAccount = {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    lastLoginAt?: string;
    banAllAccess: boolean;
    accountType: string;
    createdAt: string;
};

interface StaleAccountsProps {
    onClose?: () => void;
}

const StaleAccounts = ({ onClose }: StaleAccountsProps) => {
    const [loading, setLoading] = useState(false);
    const [loadingCount, setLoadingCount] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [data, setData] = useState<StaleAccount[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [hasMore, setHasMore] = useState(false);
    const [offset, setOffset] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteProgress, setDeleteProgress] = useState({ deleted: 0, remaining: 0 });
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const [accountTypeFilter, setAccountTypeFilter] = useState<string>('');
    const [filter18Month, setFilter18Month] = useState<boolean>(false);
    const [count18MonthIdle, setCount18MonthIdle] = useState<number>(0); // Separate count for 18-month idle accounts

    const limit = 100;

    useEffect(() => {
        loadCount();
        load18MonthIdleCount(); // Always load 18-month idle count separately
        loadData(0, '', '', false);
    }, []);

    useEffect(() => {
        // Reload data when filter18Month changes
        loadCount();
        load18MonthIdleCount(); // Also reload 18-month idle count
        loadData(0, searchKeyword, accountTypeFilter, filter18Month);
    }, [filter18Month]);

    const loadCount = async () => {
        setLoadingCount(true);
        try {
            const params: any = {};
            if (filter18Month) {
                params.filter18Month = 'true';
            }
            const response = await getStaleAccountsCount(params);
            setTotalCount(response.data.count || 0);
        } catch (error: any) {
            message.error('Failed to load stale accounts count');
            console.error(error);
        } finally {
            setLoadingCount(false);
        }
    };

    const load18MonthIdleCount = async () => {
        try {
            const params: any = { filter18Month: 'true' };
            // Include accountType filter if selected
            if (accountTypeFilter) {
                params.accountType = accountTypeFilter;
            }
            const response = await getStaleAccountsCount(params);
            setCount18MonthIdle(response.data.count || 0);
        } catch (error: any) {
            console.error('Failed to load 18-month idle accounts count:', error);
        }
    };

    const loadData = async (newOffset: number, search: string = '', accountType: string = '', filter18MonthFlag: boolean = false) => {
        setLoading(true);
        try {
            const params: any = { limit, offset: newOffset };
            if (search && search.trim()) {
                params.search = search.trim();
            }
            if (accountType) {
                params.accountType = accountType;
            }
            if (filter18MonthFlag) {
                params.filter18Month = 'true';
            }

            const response = await getStaleAccounts(params);
            const newData = response.data.users || [];

            if (newOffset === 0) {
                setData(newData);
            } else {
                setData(prev => [...prev, ...newData]);
            }

            setOffset(newOffset);
            setHasMore(newData.length === limit);
        } catch (error: any) {
            message.error('Failed to load stale accounts');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const loadMore = () => {
        if (!loading && hasMore) {
            loadData(offset + limit, searchKeyword, accountTypeFilter, filter18Month);
        }
    };

    const handleSearch = (value: string) => {
        setSearchKeyword(value);
        setSelectedRowKeys([]);
        loadData(0, value, accountTypeFilter, filter18Month);
    };

    const handleAccountTypeChange = (value: string) => {
        setAccountTypeFilter(value);
        setSelectedRowKeys([]);
        loadData(0, searchKeyword, value, filter18Month);
        loadCount(); // Reload count with new accountType filter
        load18MonthIdleCount(); // Reload 18-month idle count with new accountType filter
    };

    const handleFilter18MonthChange = (checked: boolean) => {
        setFilter18Month(checked);
        setSelectedRowKeys([]);
    };

    const handleBlock = async (userIds: string[] | null) => {
        // If userIds is null, block all; if empty array, show warning
        if (userIds !== null && userIds.length === 0) {
            message.warning('Please select at least one account or use "Block All"');
            return;
        }

        setLoading(true);
        try {
            const idsToBlock = userIds || [];
            await blockStaleAccounts(idsToBlock);
            const count = userIds === null ? totalCount : userIds.length;
            message.success(`Blocked ${count} account(s) successfully`);
            setSelectedRowKeys([]);
            loadData(0, searchKeyword, accountTypeFilter, filter18Month);
            loadCount();
            load18MonthIdleCount(); // Reload 18-month idle count after deletion
        } catch (error: any) {
            message.error('Failed to block accounts');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleUnblock = async (userIds: string[] | null) => {
        // If userIds is null, unblock all; if empty array, show warning
        if (userIds !== null && userIds.length === 0) {
            message.warning('Please select at least one account or use "Unblock All"');
            return;
        }

        setLoading(true);
        try {
            const idsToUnblock = userIds || [];
            await unblockStaleAccounts(idsToUnblock);
            const count = userIds === null ? totalCount : userIds.length;
            message.success(`Unblocked ${count} account(s) successfully`);
            setSelectedRowKeys([]);
            loadData(0, searchKeyword, accountTypeFilter, filter18Month);
            loadCount();
            load18MonthIdleCount(); // Reload 18-month idle count after unblock
        } catch (error: any) {
            message.error('Failed to unblock accounts');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete18MonthIdle = async (userIds: string[] | null) => {
        // If userIds is null, delete all 18-month idle; if empty array, show warning
        if (userIds !== null && userIds.length === 0) {
            message.warning('Please select at least one account or use "Delete All 18-Month Idle"');
            return;
        }

        setIsDeleting(true);
        // Use count18MonthIdle for "Delete All", not totalCount
        const initialCount = userIds === null ? count18MonthIdle : userIds.length;
        setDeleteProgress({ deleted: 0, remaining: initialCount });

        try {
            let deletedTotal = 0;
            let hasMoreToDelete = true;
            const isDeleteSelected = userIds !== null && userIds.length > 0;

            while (hasMoreToDelete) {
                const response = await delete18MonthIdleAccounts(isDeleteSelected ? userIds : null);
                const result = response.data;

                deletedTotal += result.deletedCount || 0;
                hasMoreToDelete = !isDeleteSelected && (result.hasMore || false);

                setDeleteProgress({
                    deleted: deletedTotal,
                    remaining: result.remainingCount ?? 0,
                });

                if (hasMoreToDelete) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }

            message.success(`Deleted ${deletedTotal} idle account(s) successfully`);
            setSelectedRowKeys([]);
            loadData(0, searchKeyword, accountTypeFilter, filter18Month);
            loadCount();
            load18MonthIdleCount(); // Reload 18-month idle count after deletion
        } catch (error: any) {
            message.error('Failed to delete idle accounts');
            console.error(error);
        } finally {
            setIsDeleting(false);
            setDeleteProgress({ deleted: 0, remaining: 0 });
        }
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys: React.Key[]) => {
            setSelectedRowKeys(selectedKeys);
        },
    };

    const columns: ColumnsType<StaleAccount> = [
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 250,
            ellipsis: true,
            render: (value: string) => (
                <Space size="small" style={{ maxWidth: '100%' }}>
                    <Link to={`/user/${value}`} style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
                        {value}
                    </Link>
                    <Link to={`/user/${value}`} target="_blank" rel="noopener noreferrer">
                        <ExportOutlined />
                    </Link>
                </Space>
            ),
        },
        {
            title: 'First Name',
            dataIndex: 'firstName',
            key: 'firstName',
        },
        {
            title: 'Last Name',
            dataIndex: 'lastName',
            key: 'lastName',
        },
        {
            title: 'Last Login',
            dataIndex: 'lastLoginAt',
            key: 'lastLoginAt',
            render: (value: any) => value ? convertDateTime(value) : 'Never',
        },
        {
            title: 'Account Type',
            dataIndex: 'accountType',
            key: 'accountType',
        },
        {
            title: 'Status',
            dataIndex: 'banAllAccess',
            key: 'banAllAccess',
            render: (value: boolean) => value ? 'Blocked' : 'Active',
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (value: any) => convertDateTime(value),
        },
    ];

    const selectedUserIds = selectedRowKeys.map(key => String(key));

    return (
        <div>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Space>
                    <Typography.Text strong>
                        {filter18Month ? 'Total 18-Month Idle Accounts' : 'Total Stale Accounts (90+ days)'}: {loadingCount ? <Spin size="small" /> : totalCount}
                    </Typography.Text>
                </Space>

                <Space>
                    <Search
                        placeholder="Search by email, first name, or last name"
                        allowClear
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        onSearch={handleSearch}
                        style={{ width: 400 }}
                        enterButton
                        loading={loading}
                    />
                    <Select
                        placeholder="Filter by Account Type"
                        allowClear
                        value={accountTypeFilter || undefined}
                        onChange={handleAccountTypeChange}
                        style={{ width: 200 }}
                    >
                        <Option value="USER">USER</Option>
                        <Option value="MEMBER">MEMBER</Option>
                    </Select>
                    <Checkbox
                        checked={filter18Month}
                        onChange={(e) => handleFilter18MonthChange(e.target.checked)}
                    >
                        Show 18-Month Idle Accounts
                    </Checkbox>
                </Space>

                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {/* Block/Unblock buttons - Only show for 90-day stale accounts */}
                    {!filter18Month && (
                        <Space>
                            {selectedUserIds.length > 0 ? (
                                <>
                                    <Button
                                        type="primary"
                                        onClick={() => handleBlock(selectedUserIds)}
                                        disabled={loading}
                                    >
                                        Block Selected ({selectedUserIds.length})
                                    </Button>
                                    <Button
                                        onClick={() => handleUnblock(selectedUserIds)}
                                        disabled={loading}
                                    >
                                        Unblock Selected ({selectedUserIds.length})
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Popconfirm
                                        title="Block All Stale Accounts"
                                        description={`This will block ALL ${totalCount} stale accounts (90+ days). This action can be undone. Continue?`}
                                        onConfirm={() => handleBlock(null)}
                                        okText="Yes, Block All"
                                        cancelText="Cancel"
                                        okButtonProps={{ danger: true }}
                                    >
                                        <Button
                                            type="primary"
                                            danger
                                            disabled={totalCount === 0 || loading}
                                        >
                                            Block All ({totalCount})
                                        </Button>
                                    </Popconfirm>
                                    <Popconfirm
                                        title="Unblock All Stale Accounts"
                                        description={`This will unblock ALL ${totalCount} stale accounts (90+ days). Continue?`}
                                        onConfirm={() => handleUnblock(null)}
                                        okText="Yes, Unblock All"
                                        cancelText="Cancel"
                                    >
                                        <Button
                                            disabled={totalCount === 0 || loading}
                                        >
                                            Unblock All ({totalCount})
                                        </Button>
                                    </Popconfirm>
                                </>
                            )}
                        </Space>
                    )}
                    {/* Delete button - Only show for 18-month idle accounts */}
                    {filter18Month && (
                        <Space>
                            <Popconfirm
                                title={selectedUserIds.length > 0 ? "Delete Selected 18-Month Idle Accounts" : "Delete All 18-Month Idle Accounts"}
                                description={selectedUserIds.length > 0
                                    ? `This will delete ${selectedUserIds.length} selected account(s) with last login > 18 months. This action cannot be undone. Continue?`
                                    : `This will delete ALL accounts with last login > 18 months (${count18MonthIdle} total). This action cannot be undone. Continue?`}
                                onConfirm={() => handleDelete18MonthIdle(selectedUserIds.length > 0 ? selectedUserIds : null)}
                                okText="Yes, Delete"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                            >
                                <Button
                                    type="primary"
                                    danger
                                    disabled={(selectedUserIds.length === 0 && count18MonthIdle === 0) || isDeleting || loading}
                                >
                                    {selectedUserIds.length > 0
                                        ? `Delete Selected 18-Month Idle (${selectedUserIds.length})`
                                        : `Delete All 18-Month Idle (${count18MonthIdle})`}
                                </Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>

                {isDeleting && (
                    <div style={{ padding: '16px', background: '#f0f0f0', borderRadius: '4px' }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Typography.Text strong>Deleting idle accounts...</Typography.Text>
                            <Typography.Text>
                                Deleted: {deleteProgress.deleted} | Remaining: {deleteProgress.remaining}
                            </Typography.Text>
                            <Spin size="large" />
                        </Space>
                    </div>
                )}

                <Table
                    className="table-striped-row"
                    columns={columns}
                    dataSource={data}
                    rowKey="_id"
                    rowSelection={rowSelection}
                    loading={loading}
                    pagination={false}
                    scroll={{ y: 600 }}
                />

                {hasMore && (
                    <div style={{ textAlign: 'center', padding: '16px' }}>
                        <Button onClick={loadMore} loading={loading}>
                            Load More ({limit} more)
                        </Button>
                    </div>
                )}
            </Space>
        </div>
    );
};

export default StaleAccounts;

